// ENGINEERING PRESENTATION CHOICE: three hours smooths isolated hourly peaks.
import { backendDayPresentation, SEA_BASS_RANKING_POLICY, MACKEREL_RANKING_POLICY } from "./seaBassPresentation";
// It is only used to select existing engine hours for display, not as a biological score.
export const PRESENTATION_PERIOD_HOURS = 3;
const HOUR_MS = 60 * 60 * 1000;

const timestampMillis = (value) => {
  const parsed = typeof value === "string" ? Date.parse(value) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

const scoreValue = (value) =>
  Number.isFinite(value?.score) && value.score >= 0 && value.score <= 100
    ? value.score
    : null;

export const hasFlatDisplayedOpportunity = (points) => {
  const displayedScores = (Array.isArray(points) ? points : [])
    .map((point) => scoreValue(point?.fishingOpportunity))
    .filter((value) => value !== null)
    .map((value) => Math.round(value));
  return (
    displayedScores.length > 1 &&
    displayedScores.every((value) => value === displayedScores[0])
  );
};

const earlierPeak = (left, right) =>
  scoreValue(left.fishingOpportunity) > scoreValue(right.fishingOpportunity)
    ? left
    : scoreValue(left.fishingOpportunity) < scoreValue(right.fishingOpportunity)
      ? right
      : timestampMillis(left.timestamp) <= timestampMillis(right.timestamp)
        ? left
        : right;

const summarizeConfidence = (points) => {
  const available = points.filter((point) => scoreValue(point.confidence) !== null);
  if (!available.length) return { mean: null, minimum: null };
  const mean =
    available.reduce((total, point) => total + point.confidence.score, 0) /
    available.length;
  const minimum = available.reduce((lowest, point) =>
    point.confidence.score < lowest.confidence.score ? point : lowest
  ).confidence;
  return { mean, minimum };
};

const rankedCandidate = (points, kind) => {
  const values = points.map((point) => point.fishingOpportunity.score);
  const confidence = summarizeConfidence(points);
  const peakPoint = points.reduce(earlierPeak);
  const startMs = timestampMillis(points[0].timestamp);
  const lastMs = timestampMillis(points[points.length - 1].timestamp);
  return {
    kind,
    points,
    startTimestamp: points[0].timestamp,
    endTimestamp:
      kind === "period" ? new Date(lastMs + HOUR_MS).toISOString() : points[0].timestamp,
    startMs,
    rankingValue: values.reduce((total, value) => total + value, 0) / values.length,
    minimumOpportunity: Math.min(...values),
    meanConfidence: confidence.mean,
    confidence: confidence.minimum,
    peakOpportunity: peakPoint.fishingOpportunity,
    peakTimestamp: peakPoint.timestamp,
  };
};

// Negative means `left` ranks ahead of `right`.
export const comparePresentationCandidates = (left, right) =>
  right.rankingValue - left.rankingValue ||
  right.minimumOpportunity - left.minimumOpportunity ||
  (right.meanConfidence ?? -1) - (left.meanConfidence ?? -1) ||
  left.startMs - right.startMs;

const compareAcrossDays = (left, right) =>
  right.strongest.rankingValue - left.strongest.rankingValue ||
  right.strongest.minimumOpportunity - left.strongest.minimumOpportunity ||
  (right.strongest.meanConfidence ?? -1) -
    (left.strongest.meanConfidence ?? -1) ||
  left.strongest.startMs - right.strongest.startMs;

export const strongestModeledPeriod = (points, { notBefore = null } = {}) => {
  const valid = (Array.isArray(points) ? points : [])
    .map((point) => ({ ...point, timestampMs: timestampMillis(point?.timestamp) }))
    .filter(
      (point) =>
        point.timestampMs !== null &&
        (notBefore === null || point.timestampMs >= notBefore) &&
        scoreValue(point.fishingOpportunity) !== null
    )
    .sort((left, right) => left.timestampMs - right.timestampMs);

  const candidates = [];
  for (let index = 0; index <= valid.length - PRESENTATION_PERIOD_HOURS; index += 1) {
    const window = valid.slice(index, index + PRESENTATION_PERIOD_HOURS);
    const contiguous = window.every(
      (point, offset) => offset === 0 || point.timestampMs - window[offset - 1].timestampMs === HOUR_MS
    );
    if (contiguous) candidates.push(rankedCandidate(window, "period"));
  }
  if (candidates.length) return candidates.sort(comparePresentationCandidates)[0];
  if (!valid.length) return null;

  const fallback = [...valid].sort((left, right) => {
    const opportunityDifference =
      right.fishingOpportunity.score - left.fishingOpportunity.score;
    if (opportunityDifference) return opportunityDifference;
    const confidenceDifference =
      (right.confidence?.score ?? -1) - (left.confidence?.score ?? -1);
    return confidenceDifference || left.timestampMs - right.timestampMs;
  })[0];
  return rankedCandidate([fallback], "hour");
};

export const buildMultiDayPresentation = (
  forecastDays,
  currentTimestamp
) => {
  const currentMs = timestampMillis(currentTimestamp);
  const ordered = (Array.isArray(forecastDays) ? forecastDays : [])
    .filter((day) => typeof day?.localDate === "string")
    .sort((left, right) => left.localDate.localeCompare(right.localDate))
    .slice(0, 3);

  const days = ordered.map((day, index) => {
    const points = (Array.isArray(day.points) ? day.points : [])
      .filter((point) => {
        const pointMs = timestampMillis(point?.timestamp);
        return pointMs !== null && (index !== 0 || currentMs === null || pointMs >= currentMs);
      })
      .sort((left, right) => timestampMillis(left.timestamp) - timestampMillis(right.timestamp));
    if ([SEA_BASS_RANKING_POLICY, MACKEREL_RANKING_POLICY].includes(day.rankingPolicy)) return backendDayPresentation(day, points);
    return {
      ...day,
      points,
      hasFlatOpportunity: hasFlatDisplayedOpportunity(points),
      strongest: strongestModeledPeriod(points),
      isBestModeledPeriod: false,
    };
  });

  if (days.some((day) => [SEA_BASS_RANKING_POLICY, MACKEREL_RANKING_POLICY].includes(day.rankingPolicy))) return days;
  const usable = days.filter((day) => day.strongest);
  if (usable.length > 1) {
    const bestLocalDate = [...usable].sort(compareAcrossDays)[0].localDate;
    return days.map((day) => ({
      ...day,
      isBestModeledPeriod: day.localDate === bestLocalDate,
    }));
  }
  return days;
};

export const multiDayForecastInternals = {
  HOUR_MS,
  timestampMillis,
};
